import pick from 'lodash/pick';

import {getEventValueAndLabel} from 'common/report/util/events';
import {findKeyByValue} from 'common/util/typescript';
import {ALL_MATH_TYPES, DATASET_MIXPANEL, PER_USER_AGGREGATIONS, ResourceType} from 'common/report/constants';
import {TOP_PEOPLE} from 'common/query-builder/query-block/properties-menu/constants';
import {
  CohortItem,
  FiltersOperator,
  isEventEntry,
  isInlineCohortItem,
  isSavedCohortItem,
  QueryMetricEntry,
} from 'common/query-builder/query-entry-section/types';
import {
  Clause,
  FilterClause,
  QUERY_BUILDER_RESOURCE_TYPE_MAPPING,
  ShowClause,
} from 'common/report/insights/models/clause';
import {ShowSection} from 'common/report/insights/models/section';
import {formatSource} from 'common/report/insights/util';
import {isProperty} from 'common/query-builder/query-block/properties-menu/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';

import {DETERMINER_MAPPING} from './constants';

/** operators in the legacy per-user format */
const LEGACY_PER_USER_OPERATORS = [
  ALL_MATH_TYPES.AVERAGE,
  ALL_MATH_TYPES.MEDIAN,
  ALL_MATH_TYPES.MAX,
  ALL_MATH_TYPES.MIN,
  ALL_MATH_TYPES.P90,
  ALL_MATH_TYPES.P75,
  ALL_MATH_TYPES.P25,
];

function getDataGroupLabel(dataGroupId, dataGroups = []) {
  const group = dataGroups.find((item) => item.groupID === dataGroupId);

  return group ? formatSource(group.displayName, {all: true, pluralizeDataGroup: true}) : TOP_PEOPLE.label;
}

/**
 * Transforms show clauses to event entries
 */
export function getEntries(showClauses: ShowClause[], dataGroups: DataGroup[] = []): QueryMetricEntry[] {
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  return showClauses.map((clause) => {
    const type = findKeyByValue(QUERY_BUILDER_RESOURCE_TYPE_MAPPING, clause.resourceType) || ResourceType.Event;
    const props = {
      type: null,
      aggregationOperator: clause.math,
      aggregationOperatorPerUser: clause.perUserAggregation,
      dataGroupId: clause.dataGroupId,
      property: null,
      event: clause.value,
      filters: clause.filter ? clause.filter.map((filterClause) => filterClause.toPropertyFilter()) : [],
      filtersOperator: clause.filter
        ? findKeyByValue(DETERMINER_MAPPING, clause.filterDeterminer)
        : FiltersOperator.And,
      readOnlyEvent: type === ResourceType.User,
      ...(clause.math === ALL_MATH_TYPES.HISTOGRAM ? {customBucket: clause.customBucket} : {}),
      ...(clause.multiAttribution ? {multiAttribution: clause.multiAttribution} : {}),
      ...(clause.rolling ? {rolling: clause.rolling} : {}),
      ...(clause.cumulative ? {cumulative: clause.cumulative} : {}),
    };

    const {value, dataGroupId} = clause;
    switch (type) {
      case ResourceType.Cohort: {
        const inlineCohort = value.raw_cohort;
        const cohortItem: CohortItem = {
          resourceType: ResourceType.Cohort,
          label: value.name,
          value: inlineCohort
            ? {cohort: pick(clause.value, `name`, `raw_cohort`, `id`)}
            : value.cohort
            ? value
            : value.id,
          ...(value.renamed ? {renamed: value.renamed} : {}),
          ...(dataGroupId && !inlineCohort ? {data_group_id: dataGroupId} : {}),
        };
        props.type = ResourceType.Cohort;
        props.event = cohortItem;
        break;
      }
      case ResourceType.User:
      case ResourceType.Event: {
        let {value: eventValue, label} = getEventValueAndLabel(value);
        // get user type dataGroupId label
        if (type === ResourceType.User) {
          if (dataGroupId) {
            label = getDataGroupLabel(dataGroupId, dataGroups || []);
          } else {
            label = TOP_PEOPLE.label;
          }
        }

        props.type = type;
        props.event = {
          custom: value.custom || false,
          label: eventValue ? label : `Select Event`,
          value: eventValue,
          ...(value.renamed ? {renamed: value.renamed} : {}),
          ...(value.id ? {id: value.id} : {}),
        };
        break;
      }
    }

    if (clause.property) {
      const {
        listItemGroup,
        customProperty,
        customPropertyId,
        dataGroupId,
        tempDataGroupId,
        defaultType,
        joinPropertyType,
        propertyObjectKey,
        name,
        resourceType: insightsResourceType,
      } = clause.property;
      const resourceType = findKeyByValue(QUERY_BUILDER_RESOURCE_TYPE_MAPPING, insightsResourceType);
      props.property = {
        resourceType,
        dataGroupId: dataGroupId ?? clause.dataGroupId,
        tempDataGroupId: tempDataGroupId ?? null,
        propertyDefaultType: defaultType,
        type: clause.property.type,
        unit: clause.property.unit ?? null,
        value: name,
        ...(propertyObjectKey ? {propertyObjectKey} : {}),
        ...(customProperty ? {customProperty} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
        ...(joinPropertyType ? {joinPropertyType} : {}),
        ...(listItemGroup ? {listItemGroup} : {}),
      };
    }

    // migrate old aggregation format
    if (!clause.property && LEGACY_PER_USER_OPERATORS.includes(props.aggregationOperator)) {
      props.aggregationOperatorPerUser = PER_USER_AGGREGATIONS.TOTAL;
    }

    return props;
  });
}

/**
 * Transforms event entries to show sections
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getShowSection(entries: QueryMetricEntry[], dataGroupId?: string): ShowSection {
  let showSection;

  if (entries.length) {
    const showClauses = entries.map((entry) => {
      const {
        event,
        filters,
        property,
        filtersOperator,
        aggregationOperator,
        type,
        aggregationOperatorPerUser,
        rolling,
        cumulative,
      } = entry;
      const filterClauses = filters.map((filter) => FilterClause.fromPropertyFilter(filter));

      let showFilterSection = {};
      if (filterClauses.length) {
        showFilterSection = {
          filter: filterClauses,
          filterDeterminer: DETERMINER_MAPPING[filtersOperator],
        };
      }

      const attrs = {
        resourceType: null,
        dataGroupId: entry.dataGroupId,
        dataset: DATASET_MIXPANEL,
        math: aggregationOperator || ShowClause.MATH_TYPE_TOTAL,
        perUserAggregation: aggregationOperatorPerUser || null,
        rolling: rolling || null,
        cumulative: cumulative || false,
        property: null,
        value: null,
        ...showFilterSection,
        ...(isEventEntry(entry) && aggregationOperator === ALL_MATH_TYPES.HISTOGRAM
          ? {customBucket: entry.customBucket}
          : {}),
        ...(isEventEntry(entry) && entry.multiAttribution ? {multiAttribution: entry.multiAttribution} : {}),
      };

      switch (type) {
        case ResourceType.Cohort: {
          attrs.resourceType = Clause.RESOURCE_TYPE_COHORTS;
          if (isInlineCohortItem(event)) {
            const cohortValue = event.value.cohort;

            attrs.value = {
              name: event.label,
              resourceType: ShowClause.RESOURCE_TYPE_COHORTS,
              ...cohortValue,
            };
          } else if (isSavedCohortItem(event)) {
            attrs.value = {
              id: event.value,
              name: event.label,
              resourceType: ShowClause.RESOURCE_TYPE_COHORTS,
              ...(event.renamed ? {renamed: event.renamed} : {}),
            };
          }
          break;
        }
        case ResourceType.User:
        case ResourceType.Event:
          attrs.resourceType = QUERY_BUILDER_RESOURCE_TYPE_MAPPING[type];
          attrs.value = {
            dataset: DATASET_MIXPANEL,
            custom: event.custom,
            resourceType: attrs.resourceType,
            // Insights uses friendly name for custom event
            name: event.custom ? event.label : event.value,
            ...(event.id ? {id: event.id} : {}),
            ...(event.renamed ? {renamed: event.renamed} : {}),
          };
          break;
      }

      if (property) {
        const {resourceType, value} = property;
        let customProperty,
          customPropertyId,
          dataGroupId,
          tempDataGroupId,
          joinPropertyType,
          propertyDefaultType,
          propertyObjectKey,
          type,
          unit,
          listItemGroup;

        if (isProperty(property)) {
          ({
            customProperty,
            customPropertyId,
            dataGroupId,
            tempDataGroupId,
            joinPropertyType,
            propertyDefaultType,
            propertyObjectKey,
            type,
            unit,
            listItemGroup,
          } = property);
        }
        attrs.property = {
          ...(customProperty ? {customProperty} : {}),
          ...(customPropertyId ? {customPropertyId} : {}),
          dataset: DATASET_MIXPANEL,
          ...(dataGroupId ? {dataGroupId} : {}),
          ...(tempDataGroupId ? {tempDataGroupId} : {}),
          ...(joinPropertyType ? {joinPropertyType} : {}),
          ...(listItemGroup ? {listItemGroup} : {}),
          defaultType: propertyDefaultType,
          ...(propertyObjectKey ? {propertyObjectKey} : {}),
          name: value,
          resourceType: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[resourceType],
          type,
          unit: unit ?? null,
        };
      }

      return new ShowClause(attrs);
    });

    showSection = new ShowSection(showClauses);
  } else {
    showSection = ShowSection.create({dataGroupId});
  }

  return showSection;
}
